import { hasGroups, isLicenseYearAnchored, resolveCycle } from "./cmeCycle";

export function cycleDateString(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function localDate(value) {
    if (!value) return null;
    const date = value instanceof Date
        ? new Date(value.getFullYear(), value.getMonth(), value.getDate())
        : new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
}

function shiftYears(date, years) {
    const shifted = new Date(date.getFullYear() + years, date.getMonth(), date.getDate());
    return shifted.getMonth() === date.getMonth()
        ? shifted
        : new Date(date.getFullYear() + years, date.getMonth() + 1, 0);
}

// Some source rules select this year's upcoming anniversary. The tracker must
// keep the cycle containing today active until its last day has passed.
export function resolveContainingCycle(rule, options = {}) {
    if (hasGroups(rule) && !options.groupLabel) return null;
    const referenceDate = localDate(options.referenceDate || new Date());
    if (!referenceDate) return null;
    const resolved = resolveCycle(rule, { ...options, referenceDate });
    if (!resolved?.start || !resolved?.end) return null;
    let { start, end } = resolved;
    const licenseDate = localDate(options.personalDateStr);
    if (
        isLicenseYearAnchored(rule)
        && licenseDate
        && licenseDate <= referenceDate
        && referenceDate < start
    ) {
        return { start, end };
    }
    // Use the actual date span: some source rules have a cycle length that
    // disagrees with their explicit start/end year rules.
    const years = Math.max(1, Math.round((end - start) / (365.25 * 86400000)));
    for (let i = 0; i < 100; i += 1) {
        if (start <= referenceDate && referenceDate <= end) return { start, end };
        const direction = referenceDate < start ? -years : years;
        start = shiftYears(start, direction);
        end = shiftYears(end, direction);
    }
    return null;
}

export function cycleKey(cycle) {
    return `${cycleDateString(cycle.start)}_${cycleDateString(cycle.end)}`;
}

export function cycleRangeLabel(cycle, short = false, monthYearOnly = false) {
    const format = (date) => date.toLocaleDateString("en-US", {
        month: short ? "short" : "long",
        day: monthYearOnly ? undefined : "numeric",
        year: "numeric",
    });
    return `${format(cycle.start)} – ${format(cycle.end)}`;
}

export function getUploadCycle(row, rule, options = {}) {
    const start = localDate(row.cycleStart);
    const end = localDate(row.cycleEnd);
    if (start && end && start <= end) return { start, end };
    // Existing certificates have no cycle snapshot. Their completion dates
    // place them in history without deleting or duplicating stored records.
    const date = localDate(row.dateRaw || row.date);
    return date ? resolveContainingCycle(rule, { ...options, referenceDate: date }) : null;
}

export function buildCycleHistory(rows, rule, options = {}) {
    const current = resolveContainingCycle(rule, options);
    if (!current) return { current: null, currentRows: rows, previous: [] };
    const currentKey = cycleKey(current);
    const referenceDate = localDate(options.referenceDate || new Date());
    const currentRows = [];
    const previous = new Map();
    for (const row of rows) {
        const cycle = getUploadCycle(row, rule, options);
        // Keep unclassifiable legacy records accessible while dates are missing.
        if (!cycle || cycleKey(cycle) === currentKey) {
            currentRows.push(row);
        } else if (cycle.end < referenceDate) {
            const key = cycleKey(cycle);
            if (!previous.has(key)) previous.set(key, { ...cycle, key, rows: [] });
            previous.get(key).rows.push(row);
        } else if (cycle.start <= referenceDate) {
            // A changed license setting can alter the active date range. Keep
            // already saved records visible until their stored cycle ends.
            currentRows.push(row);
        }
    }
    return {
        current, currentRows,
        previous: [...previous.values()].sort((a, b) => b.end - a.end),
    };
}
